/**
 * V37.39 — Onglet « Journal Comptable » : recense toutes les rectifications
 * de date de paiement effectuees via la modale V37.38, avec filtres
 * (date, reference, utilisateur) et export CSV / XLSX.
 *
 * Endpoint backend : GET /api/domium/subscribe/admin/journal-comptable
 *   ?date_debut=YYYY-MM-DD&date_fin=YYYY-MM-DD&reference=&user_email=
 *   &export=csv|xlsx
 */
import { DatePickerFr } from '../ui/DatePickerFr';
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BookOpen, CalendarDays, Download, FileSpreadsheet, FileText, Loader2, RefreshCw, Search } from 'lucide-react';
import { API, formatDate } from './constants';

export default function JournalComptableTab({ getAuthHeader }) {
  const [filters, setFilters] = useState({
    date_debut: '',
    date_fin: '',
    reference: '',
    user_email: '',
  });
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState({});

  const buildUrl = useCallback((exportFmt = null) => {
    const params = new URLSearchParams();
    if (filters.date_debut) params.append('date_debut', filters.date_debut);
    if (filters.date_fin) params.append('date_fin', filters.date_fin);
    if (filters.reference) params.append('reference', filters.reference);
    if (filters.user_email) params.append('user_email', filters.user_email);
    if (exportFmt) params.append('export', exportFmt);
    const qs = params.toString();
    return `${API}/domium/subscribe/admin/journal-comptable${qs ? `?${qs}` : ''}`;
  }, [filters]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(buildUrl(), { headers: getAuthHeader() });
      setEntries(res.data?.entries || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement du journal');
    } finally {
      setLoading(false);
    }
  }, [buildUrl, getAuthHeader]);

  const handleExport = async (fmt) => {
    setExporting((p) => ({ ...p, [fmt]: true }));
    try {
      const res = await axios.get(buildUrl(fmt), {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const mime = fmt === 'csv'
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.download = `journal_comptable_domium_${today}.${fmt}`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Journal exporte (${fmt.toUpperCase()})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Erreur export ${fmt}`);
    } finally {
      setExporting((p) => ({ ...p, [fmt]: false }));
    }
  };

  const setMoisCourant = () => {
    const now = new Date();
    const debut = new Date(now.getFullYear(), now.getMonth(), 1);
    const fin = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setFilters((p) => ({
      ...p,
      date_debut: debut.toISOString().split('T')[0],
      date_fin: fin.toISOString().split('T')[0],
    }));
  };

  const reset = () => {
    setFilters({ date_debut: '', date_fin: '', reference: '', user_email: '' });
  };

  return (
    <div className="space-y-4" data-testid="journal-comptable-tab">
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <BookOpen className="h-5 w-5" />
              Journal Comptable — Rectifications de dates de paiement
            </CardTitle>
            <Badge className="bg-amber-600 text-white border-amber-800 font-semibold">
              {total} {total > 1 ? 'rectifications' : 'rectification'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-amber-800">
            Toutes les modifications de date de paiement enregistrees via la
            fonction « Modifier date paiement » sont tracees ici (audit comptable).
          </p>
        </CardContent>
      </Card>

      {/* Filtres */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500">Date debut</Label>
              <DatePickerFr
                value={filters.date_debut}
                onChange={(v) => setFilters((p) => ({ ...p, date_debut: v }))}
                className="w-full"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Date fin</Label>
              <DatePickerFr
                value={filters.date_fin}
                onChange={(v) => setFilters((p) => ({ ...p, date_fin: v }))}
                className="w-full"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Reference</Label>
              <Input
                placeholder="DOM-..."
                value={filters.reference}
                onChange={(e) => setFilters((p) => ({ ...p, reference: e.target.value }))}
                data-testid="jc-reference"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Utilisateur</Label>
              <Input
                placeholder="email partiel"
                value={filters.user_email}
                onChange={(e) => setFilters((p) => ({ ...p, user_email: e.target.value }))}
                data-testid="jc-user-email"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchEntries} disabled={loading} size="sm" data-testid="jc-search">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
                Rechercher
              </Button>
              <Button onClick={setMoisCourant} variant="outline" size="sm" title="Mois courant">
                <CalendarDays className="h-4 w-4" />
              </Button>
              <Button onClick={reset} variant="outline" size="sm" title="Reinitialiser">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex gap-2 mt-4 pt-3 border-t">
            <Button
              onClick={() => handleExport('csv')}
              disabled={exporting.csv || total === 0}
              variant="outline" size="sm"
              data-testid="jc-export-csv"
              className="text-xs"
            >
              {exporting.csv ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
              Export CSV
            </Button>
            <Button
              onClick={() => handleExport('xlsx')}
              disabled={exporting.xlsx || total === 0}
              variant="outline" size="sm"
              data-testid="jc-export-xlsx"
              className="text-xs border-emerald-300 text-emerald-700"
            >
              {exporting.xlsx ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-500" />
              <p className="text-sm text-gray-500 mt-2">Chargement du journal...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-700 font-medium">Aucune rectification trouvee</p>
              <p className="text-xs text-gray-400 mt-2">
                Cliquez sur <strong>« Rechercher »</strong> pour afficher les rectifications.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Modifiez une date de paiement depuis l&apos;onglet Paiements pour la voir apparaitre ici.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="jc-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-semibold text-gray-700">Date modif</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Reference</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Souscripteur</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Ancienne date</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Nouvelle date</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Motif</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Utilisateur</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, idx) => (
                    <tr key={`${e.reference}-${e.timestamp}-${idx}`}
                      className="border-b hover:bg-amber-50/50"
                      data-testid={`jc-row-${idx}`}>
                      <td className="p-3 text-xs text-gray-600 font-mono whitespace-nowrap">
                        {e.timestamp ? new Date(e.timestamp).toLocaleString('fr-FR') : '-'}
                      </td>
                      <td className="p-3 font-mono text-xs text-indigo-700">{e.reference || '-'}</td>
                      <td className="p-3 text-gray-800">{e.souscripteur || '-'}</td>
                      <td className="p-3">
                        <Badge variant="outline" className="text-xs text-red-700 border-red-200 line-through">
                          {formatDate(e.ancienne_date_paiement) || '-'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge className="text-xs bg-emerald-700 text-white border-emerald-900 font-semibold">
                          {formatDate(e.nouvelle_date_paiement) || '-'}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs text-gray-600 italic max-w-xs truncate" title={e.motif || ''}>
                        {e.motif || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="p-3 text-xs text-gray-500">{e.by || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
